import { CloseOutlined } from '@ant-design/icons';
import { useResolutionInfo } from '@mm/hooks';
import { LeadsSearchQuery } from '@mm/state/interfaces/leads';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  selectPurchasableLeads,
  selectPurchasableLeadsCount,
} from '@mm/state/selectors/leadsSearchSelectors';
import { abbreviateNumber } from '@mm/utils';
import { InputNumber, Pagination, PaginationProps } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridLabels,
  GridRow,
} from '../../../../../../../common/components/Grid/Grid';
import { TABLET_SMALL } from '../../../../../../constants/screenSize';
import { useConvertLeadsToContacts } from '../use-convert-leads-to-contacts';
import arrow from './down-left-pointing-arrow.png';
import styles from './PurchaseContactsTable.module.css';
import {
  MOBILE_PURCHASE_LEADS_CELL_DATA,
  PURCHASE_LEADS_CELL_DATA,
} from './render-purchase-table-cells';
import qualifiedLeadsSrc from './generate-qualified-leads.png';
import { Tooltip } from '@mm/components';

type Props = {
  filterParams: LeadsSearchQuery;
  setFilterParams: (params: LeadsSearchQuery) => void;
};

export const LEAD_COST_IN_CREDITS = 25;
export const MAX_CONTACTS_ALLOWED = 10000;

export const PurchaseContactsTable = ({
  filterParams,
  setFilterParams,
}: Props) => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const purchasableLeads = useSelector(selectPurchasableLeads) || [];
  const leadsCount = useSelector(selectPurchasableLeadsCount);
  const purchasableLeadsCount = leadsCount?.value || 0;
  const DEFAULT_PAGE_SIZE =
    purchasableLeads.length > 50 ? purchasableLeads.length : 50;
  const relationGreaterThenMaxDisplayed = leadsCount?.relation === 'gte';
  const hasLeads = purchasableLeads?.length;
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
  const [excludedLeadIds, setExcludedLeadIds] = useState<string[]>([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const noSelectedLeads = !selectedCheckboxes.length && !selectedAll;
  const allLoadedLeadsPurchased =
    purchasableLeadsCount <= DEFAULT_PAGE_SIZE &&
    !purchasableLeads.filter(lead => !lead.converted).length;

  const defaultSelectedLeadsCount =
    purchasableLeadsCount > MAX_CONTACTS_ALLOWED
      ? MAX_CONTACTS_ALLOWED
      : purchasableLeadsCount;
  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage?.subscription);
  const [selectedLeadsCount, setSelectedLeadsCount] = useState<number>(0);

  const invalidSelectedLeadsCount =
    Number(selectedLeadsCount) > purchasableLeadsCount || !selectedLeadsCount;

  const totalLeadsCost =
    (selectedLeadsCount || MAX_CONTACTS_ALLOWED) * LEAD_COST_IN_CREDITS;

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;

  const GRID_TEMPLATE_COLUMNS = isMobile
    ? '1fr 1fr 1fr 1fr 1fr 1fr 1fr'
    : '1fr 1.5fr 0.75fr 0.5fr 1.25fr 1fr 1fr';

  const { convertLeads } = useConvertLeadsToContacts({ filterParams });

  const isCheckboxChecked = (permIndividualId: string, idx: number) => {
    if (selectedAll) {
      const numberOfExcludedTillCurrentIdx = purchasableLeads
        .slice(0, idx + 1 + Number(filterParams?.offset))
        .filter(l => excludedLeadIds.includes(l.permIndividualId)).length;
      return (
        !excludedLeadIds.some(checkboxId => checkboxId === permIndividualId) &&
        idx + 1 + Number(filterParams?.offset) <=
          selectedLeadsCount + numberOfExcludedTillCurrentIdx
      );
    }
    return selectedCheckboxes.some(
      checkboxId => checkboxId === permIndividualId,
    );
  };

  const isCheckboxDisabled = (idx: number) => {
    if (selectedAll) {
      const numberOfExcludedTillCurrentIdx = purchasableLeads
        .slice(0, idx + 1 + Number(filterParams?.offset))
        .filter(l => excludedLeadIds.includes(l.permIndividualId)).length;
      return (
        idx + 1 + Number(filterParams?.offset) >
        selectedLeadsCount + numberOfExcludedTillCurrentIdx
      );
    }
  };

  const onCheckboxClick = (permIndividualId: string, idx: number) => {
    const isExisting = isCheckboxChecked(permIndividualId, idx);

    if (selectedAll) {
      if (isExisting) {
        setExcludedLeadIds(current => [...current, permIndividualId]);

        setSelectedLeadsCount(prev => (prev > 0 ? prev - 1 : 0));
      } else {
        setExcludedLeadIds(current => [
          ...current.filter(checkboxId => checkboxId !== permIndividualId),
        ]);

        setSelectedLeadsCount(prev => prev + 1);
      }
      return;
    }

    if (isExisting) {
      setSelectedCheckboxes(current => [
        ...current.filter(checkboxId => checkboxId !== permIndividualId),
      ]);
      setSelectedLeadsCount(prev => (prev > 0 ? prev - 1 : 0));
    } else {
      setSelectedCheckboxes(current => [...current, permIndividualId]);
      setSelectedLeadsCount(prev => prev + 1);
    }
  };

  const onSelectAllClick = () => {
    setSelectedAll(!selectedAll);
    setSelectedCheckboxes([]);
    setSelectedLeadsCount(defaultSelectedLeadsCount);
  };

  const getUniqueIds = (values: string[]) => [...new Set(values)];

  const onPurchaseClick = async (e?: Event, permIndividualId?: string) => {
    e?.stopPropagation();

    if (permIndividualId) {
      setSelectedCheckboxes(current => [...current, permIndividualId]);
    }

    const params = selectedAll
      ? {
          searchBody: {
            query: filterParams.query,
            exclude: filterParams.exclude,
            limit: selectedLeadsCount + excludedLeadIds.length,
          },
          excludedLeadIds,
        }
      : {
          leadIds: permIndividualId
            ? getUniqueIds([...selectedCheckboxes, permIndividualId])
            : getUniqueIds([...selectedCheckboxes]),
        };

    await convertLeads(params);
  };

  const onResetSelectAll = () => {
    setSelectedAll(false);
    setSelectedLeadsCount(0);
    setSelectedCheckboxes([]);
    setExcludedLeadIds([]);
  };

  const [currentPage, setCurrentPage] = useState(1);

  const onChange: PaginationProps['onChange'] = page => {
    setCurrentPage(page);
    setFilterParams({
      ...filterParams,
      offset: (page - 1) * DEFAULT_PAGE_SIZE,
    });
  };

  useEffect(() => {
    setSelectedCheckboxes([]);
    setSelectedAll(false);

    onResetSelectAll();
  }, [filterParams.query, filterParams.exclude]);

  return (
    <div className={styles.container}>
      <div className={styles.bar}>
        {!!purchasableLeadsCount && (
          <div className={styles.leadsCount}>
            {purchasableLeadsCount.toLocaleString('en-US')}
            {relationGreaterThenMaxDisplayed ? '+' : ''} filtered
          </div>
        )}
        {!!hasLeads && (
          <div className={styles.numberInputWrapper}>
            <InputNumber
              min={1}
              max={MAX_CONTACTS_ALLOWED}
              value={selectedLeadsCount}
              onChange={value => {
                setSelectedCheckboxes([]);
                setExcludedLeadIds([]);
                if (!selectedAll) {
                  onSelectAllClick();
                }
                setSelectedLeadsCount(value || 1);
              }}
              status={invalidSelectedLeadsCount ? 'error' : undefined}
              disabled={purchasableLeadsCount === 0}
            />
            selected
            <CloseOutlined
              className={styles.closeIcon}
              onClick={onResetSelectAll}
            />
          </div>
        )}

        {!selectedAll && (
          <PrimaryButton
            disabled={!hasLeads}
            className={styles.secondaryButton}
            onClick={onSelectAllClick}
            color="secondary-blue"
          >
            Select All
          </PrimaryButton>
        )}

        <Tooltip
          title={`${abbreviateNumber(totalLeadsCost)} Credits`}
          placement="topRight"
          overlayClassName={classNames(styles.tip, {
            [styles.hide]:
              noSelectedLeads ||
              allLoadedLeadsPurchased ||
              isNewBillingSubscription,
          })}
        >
          <div>
            <PrimaryButton
              className={styles.primaryButton}
              disabled={
                !hasLeads ||
                noSelectedLeads ||
                allLoadedLeadsPurchased ||
                invalidSelectedLeadsCount
              }
              onClick={() => onPurchaseClick()}
            >
              Purchase Leads
            </PrimaryButton>
          </div>
        </Tooltip>
      </div>
      <Grid
        className={classNames(styles.grid, {
          [styles.gridEmptyState]: !hasLeads,
        })}
      >
        {isMobile ? null : (
          <GridLabels
            className={classNames(styles.gridLabels, {
              [styles.gridLabelsEmptyState]: !hasLeads,
            })}
            gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
          >
            {PURCHASE_LEADS_CELL_DATA.map(item => {
              const isFirstNameColumns = item.key === 'firstname';
              return (
                <div
                  className={classNames(styles.tableHeader, {
                    [styles.firstNameHeading]: isFirstNameColumns,
                  })}
                  key={item.key}
                >
                  {item.label}
                </div>
              );
            })}
          </GridLabels>
        )}
        <div className={isMobile ? styles.gridRowsMobile : styles.gridRows}>
          {purchasableLeads?.map((lead, idx) => {
            const isChecked = isCheckboxChecked(lead.permIndividualId, idx);
            const isDisabled = isCheckboxDisabled(idx);

            const onClick = () => {
              if (lead.converted || isDisabled) {
                return;
              }
              onCheckboxClick(lead.permIndividualId, idx);
            };
            return isMobile ? (
              <GridRow
                key={lead.permIndividualId}
                className={classNames(styles.gridRowMobile, {
                  [styles.gridRowSelectedMobile]: isChecked,
                  [styles.gridRowConvertedLeadMobile]: lead.converted,
                  [styles.disabledGridRow]: isDisabled,
                })}
                gridTemplateColumns="1fr 1fr"
                onClick={onClick}
              >
                {MOBILE_PURCHASE_LEADS_CELL_DATA.map(({ Cell, key }) =>
                  Cell({
                    label: [
                      lead.firstname,
                      lead.city,
                      lead.state,
                      lead.occupation,
                      lead.sex,
                      lead.age,
                    ]
                      .filter(Boolean)
                      .join(', '),
                    isConverted: lead.converted,
                    key,
                    isChecked,
                    disabled: isDisabled,
                    onClick: e => onPurchaseClick(e, lead.permIndividualId),
                  }),
                )}
              </GridRow>
            ) : (
              <GridRow
                key={lead.permIndividualId}
                className={classNames(styles.gridRow, {
                  [styles.gridRowSelected]: isChecked,
                  [styles.gridRowConvertedLead]: lead.converted,
                  [styles.disabledGridRow]: isDisabled,
                })}
                gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
                onClick={onClick}
              >
                {PURCHASE_LEADS_CELL_DATA.map(({ Cell, key }) =>
                  Cell({
                    label: lead[key],
                    isConverted: lead.converted,
                    key,
                    isChecked,
                    disabled: isDisabled,
                    onClick: e => onPurchaseClick(e, lead.permIndividualId),
                  }),
                )}
              </GridRow>
            );
          })}
        </div>
      </Grid>
      {Boolean(hasLeads) && (
        <div className={styles.paginationWrapper}>
          <Pagination
            current={currentPage}
            onChange={onChange}
            pageSize={DEFAULT_PAGE_SIZE}
            showSizeChanger={false}
            total={purchasableLeadsCount}
            hideOnSinglePage
          />
        </div>
      )}
      {!hasLeads && (
        <>
          <div className={styles.emptyStateContainer}>
            <div className={styles.emptyStateText}>
              Select filters to find leads!
            </div>
            <img src={arrow} className={styles.emptyStateArrow} />
          </div>
          <div className={styles.descriptionBlock}>
            <div className={styles.emptyStateDescription}>
              <div>Consumer directory</div>
              <div className={styles.emptyStateSmallText}>
                Consumer directory is our lead database of over{' '}
                <span style={{ fontWeight: 500 }}>
                  250,000,000 consumers in the United States.
                </span>
              </div>
              <div className={styles.emptyStateSmallText}>
                Filter, search, and purchase consumer directory leads and add
                them to a custom outreach automation to qualify and close leads.
              </div>
            </div>
            <img
              src={qualifiedLeadsSrc}
              className={styles.emptyStateDescriptionImage}
            />
          </div>
        </>
      )}
    </div>
  );
};
